import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_INVENTORY_CONDITIONS = 'FETCH_INVENTORY_CONDITIONS';
export const FETCH_INVENTORY_CONDITIONS_SUCCESS = 'FETCH_INVENTORY_CONDITIONS_SUCCESS';
export const FETCH_INVENTORY_CONDITIONS_FAIL = 'FETCH_INVENTORY_CONDITIONS_FAIL';
export const CLEAR_INVENTORY_CONDITIONS = 'CLEAR_INVENTORY_CONDITIONS';
export const CREATE_INVENTORY_CONDITION = 'CREATE_INVENTORY_CONDITION';
export const CREATE_INVENTORY_CONDITION_SUCCESS = 'CREATE_INVENTORY_CONDITION_SUCCESS';
export const CREATE_INVENTORY_CONDITION_FAIL = 'CREATE_INVENTORY_CONDITION_FAIL';
export const UPDATE_INVENTORY_CONDITION = 'UPDATE_INVENTORY_CONDITION';
export const UPDATE_INVENTORY_CONDITION_SUCCESS = 'UPDATE_INVENTORY_CONDITION_SUCCESS';
export const UPDATE_INVENTORY_CONDITION_FAIL = 'UPDATE_INVENTORY_CONDITION_FAIL';
export const DELETE_INVENTORY_CONDITION = 'DELETE_INVENTORY_CONDITION';
export const DELETE_INVENTORY_CONDITION_SUCCESS = 'DELETE_INVENTORY_CONDITION_SUCCESS';
export const DELETE_INVENTORY_CONDITION_FAIL = 'DELETE_INVENTORY_CONDITION_FAIL';

// Reducer
export default function inventoryConditionsReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_INVENTORY_CONDITIONS:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_INVENTORY_CONDITIONS_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INVENTORY_CONDITIONS_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_INVENTORY_CONDITIONS:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_INVENTORY_CONDITION_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_INVENTORY_CONDITION_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_INVENTORY_CONDITION_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchInventoryConditions(payload) {
  return { type: FETCH_INVENTORY_CONDITIONS, payload };
}

export function fetchInventoryConditionsSuccess(payload) {
  return { type: FETCH_INVENTORY_CONDITIONS_SUCCESS, payload };
}

export function fetchInventoryConditionsFail(payload) {
  return { type: FETCH_INVENTORY_CONDITIONS_FAIL, payload };
}

export function clearInventoryConditions(payload) {
  return { type: CLEAR_INVENTORY_CONDITIONS, payload };
}

export function createInventoryCondition(payload) {
  return { type: CREATE_INVENTORY_CONDITION, payload };
}

export function createInventoryConditionSuccess(payload) {
  return { type: CREATE_INVENTORY_CONDITION_SUCCESS, payload };
}

export function createInventoryConditionFail(payload) {
  return { type: CREATE_INVENTORY_CONDITION_FAIL, payload };
}

export function updateInventoryCondition(payload) {
  return { type: UPDATE_INVENTORY_CONDITION, payload };
}

export function updateInventoryConditionSuccess(payload) {
  return { type: UPDATE_INVENTORY_CONDITION_SUCCESS, payload };
}

export function updateInventoryConditionFail(payload) {
  return { type: UPDATE_INVENTORY_CONDITION_FAIL, payload };
}

export function deleteInventoryCondition(payload) {
  return { type: DELETE_INVENTORY_CONDITION, payload };
}

export function deleteInventoryConditionSuccess(payload) {
  return { type: DELETE_INVENTORY_CONDITION_SUCCESS, payload };
}

export function deleteInventoryConditionFail(payload) {
  return { type: DELETE_INVENTORY_CONDITION_FAIL, payload };
}

// Requests

let abort;

export function requestFetchInventoryConditions(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload
    ? abortableCallApi(`inventory/condition/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('inventory/condition/');
  abort = request.abort;
  return request.res;
}

export function requestSearchInventoryConditions({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('inventory/condition/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateInventoryCondition(payload) {
  return callApi('inventory/condition/', 'POST', payload);
}

export function requestUpdateInventoryCondition(payload) {
  return callApi('inventory/condition/', 'PATCH', payload);
}

export function requestDeleteInventoryCondition(payload) {
  return callApi(`inventory/condition/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchInventoryConditionsSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchInventoryConditions, action.payload)
      : yield call(requestFetchInventoryConditions, action.payload);
    yield put(fetchInventoryConditionsSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchInventoryConditionsFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createInventoryConditionSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateInventoryCondition, values);
    yield put(createInventoryConditionSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Inventory Condition successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createInventoryConditionFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateInventoryConditionSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateInventoryCondition, values);
    yield put(updateInventoryConditionSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Inventory Condition successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateInventoryConditionFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteInventoryConditionSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteInventoryCondition, ID);
    yield put(deleteInventoryConditionSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Inventory Condition successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteInventoryConditionFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Inventory Condition',
      variant: 'warning',
    }));
  }
}

export function* inventoryConditionsSaga() {
  yield takeLatest(FETCH_INVENTORY_CONDITIONS, fetchInventoryConditionsSaga);
  yield takeEvery(CREATE_INVENTORY_CONDITION, createInventoryConditionSaga);
  yield takeEvery(UPDATE_INVENTORY_CONDITION, updateInventoryConditionSaga);
  yield takeEvery(DELETE_INVENTORY_CONDITION, deleteInventoryConditionSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/inventoryConditions.js